import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { MultiSearchResponse, TypesenseSearchResponse } from '~/types/typesense-search';
import type { TypesenseQueryParams } from '~/utils/shipmentSearch/typesense-adapters';
import type { CarrierCountsParams, CarrierCountsResponse } from '~/types/carrier-counts';
import { FACET_FIELDS } from '~/utils/shipmentSearch/typesense-constants';
import axios from 'axios';

// NOTE: If we need more than 2500 facet values, we can increase this.
const MAX_FACET_VALUES = 2500;
const TIMEOUT = 10000;
const API_ENDPOINT = '/search-proxy/api/v1/typesense/shipment-search/search';
const CARRIER_COUNTS_ENDPOINT = '/search-proxy/api/v1/typesense/shipment-search/carrier-counts';
const MULTI_SEARCH_ENDPOINT = '/search-proxy/api/v1/typesense/multi_search';
const SHIPMENT_SEARCH_COLLECTION = 'shipment-search';

const ERROR_MESSAGES = {
  404: (context: string) => `404: ${context} service not found.`,
  500: (context: string) => `500: ${context} service error. Please try again later.`,
  timeout: (context: string) => `Timeout: ${context} request took too long. Please try again.`,
  generic: (status: number, message: string, context: string) => `${status}: ${message || `${context} request failed`}`,
};

function handleTypesenseError(error: unknown, context: string): never {
  console.error(`Typesense ${context} error:`, error);

  if (!axios.isAxiosError(error)) {
    throw error;
  }

  if (error.response?.status === 404) {
    throw new Error(ERROR_MESSAGES[404](context));
  }
  if (error.response?.status === 500) {
    throw new Error(ERROR_MESSAGES[500](context));
  }
  if (error.code === 'ECONNABORTED') {
    throw new Error(ERROR_MESSAGES.timeout(context));
  }
  if (error.response?.status) {
    throw new Error(ERROR_MESSAGES.generic(error.response.status, error.message, context));
  }

  throw error;
}

async function makeTypesenseRequest<T>(endpoint: string, params: Record<string, unknown>, context: string): Promise<T> {
  try {
    const { data } = await GatewayAxiosClient.get(endpoint, {
      params,
      timeout: TIMEOUT,
    });
    return data;
  } catch (error: unknown) {
    return handleTypesenseError(error, context);
  }
}

async function postTypesenseRequest<T>(endpoint: string, body: unknown, context: string): Promise<T> {
  try {
    const { data } = await GatewayAxiosClient.post(endpoint, body, { timeout: TIMEOUT });
    return data;
  } catch (error: unknown) {
    return handleTypesenseError(error, context);
  }
}

export const shipmentTypesenseService = {
  search: {
    queryKey: 'shipment-typesense/search',
    configurationType: 'no-cache' as const,
    fn: async (params: TypesenseQueryParams): Promise<TypesenseSearchResponse> => {
      return makeTypesenseRequest(API_ENDPOINT, params, 'search');
    },
  },
  fetchFacets: {
    queryKey: 'shipment-typesense/facets',
    configurationType: 'no-cache' as const,
    fn: async (params: TypesenseQueryParams): Promise<TypesenseSearchResponse> => {
      const facetParams = {
        ...params,
        q: params.q || '*',
        query_by: params.query_by || '*',
        facet_by: FACET_FIELDS.join(','),
        max_facet_values: MAX_FACET_VALUES,
        per_page: 0,
      };

      return makeTypesenseRequest(API_ENDPOINT, facetParams, 'facets');
    },
  },
  carrierCounts: {
    queryKey: 'shipment-typesense/carrier-counts',
    configurationType: 'no-cache' as const,
    fn: async (params: CarrierCountsParams): Promise<CarrierCountsResponse> => {
      return makeTypesenseRequest(CARRIER_COUNTS_ENDPOINT, params, 'carrier-counts');
    },
  },
  multiSearch: {
    queryKey: 'shipment-typesense/multi-search',
    configurationType: 'no-cache' as const,
    fn: async (partnerSlug: string, searches: TypesenseQueryParams[]): Promise<MultiSearchResponse> => {
      const body = {
        searches: searches.map((s) => ({
          collection: `${SHIPMENT_SEARCH_COLLECTION}-${partnerSlug}`,
          ...s,
        })),
      };
      return postTypesenseRequest(MULTI_SEARCH_ENDPOINT, body, 'multi-search');
    },
  },
};
